import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { User, Home, Megaphone, Shield, Plus, Trash2, Loader2, CheckCircle2, Clock, Save, Pencil, Crown, Building2, Gift, FileText, Briefcase, BadgeCheck, MessageSquare, BarChart3 } from "lucide-react";
import { Navbar, Footer } from "@/components/Navbar";
import { FileUpload } from "@/components/FileUpload";
import { MessagesTab } from "@/components/MessagesTab";
import { ControlPanel } from "@/components/ControlPanel";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function Field({ label, children }) {
  return <div><label className="text-sm font-medium text-[#1c1917]">{label}</label><div className="mt-1.5">{children}</div></div>;
}
const inputCls = "w-full bg-white border border-[#e7e5e4] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f]";
const STRIPE_PROMO_LINK = "https://buy.stripe.com/eVq3cv0qPb9KaoY6m86Vq02";
const withRef = (link, user) => (user?.id ? `${link}?client_reference_id=${encodeURIComponent(user.id)}` : link);
const isValidCPF = (cpf) => {
  const d = (cpf || "").replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  for (const i of [9, 10]) {
    let s = 0;
    for (let n = 0; n < i; n++) s += parseInt(d[n]) * (i + 1 - n);
    let c = (s * 10) % 11;
    if (c === 10) c = 0;
    if (c !== parseInt(d[i])) return false;
  }
  return true;
};
const formatCPF = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
};
const fmtDate = (s) => (s ? new Date(s).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

// ---------- Profile Tab ----------
function ProfileTab({ meta }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", sector: "", city: "", region: "", phone: "", whatsapp: "", contact_email: "", bio: "", services: [], photo_url: "" });
  const [servicesText, setServicesText] = useState("");
  const [photo, setPhoto] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pmeta, setPmeta] = useState(null);

  const load = useCallback(() => {
    api.get("/me/professional").then((r) => {
      if (r.data) {
        setForm((f) => ({ ...f, ...r.data }));
        setServicesText((r.data.services || []).join(", "));
        if (r.data.photo_url) setPhoto([{ url: r.data.photo_url, filename: "foto", category: "photo" }]);
        setPmeta({ id: r.data.id, created_at: r.data.created_at, updated_at: r.data.updated_at, updates: r.data.updates || [] });
      } else {
        setPmeta(null);
      }
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, services: servicesText.split(",").map((s) => s.trim()).filter(Boolean), photo_url: photo[0]?.url || "" };
      await api.post("/me/professional", payload);
      toast.success("Perfil salvo! Você já aparece no diretório.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const removeProfile = async () => {
    if (!window.confirm("Tem certeza que deseja excluir o seu perfil profissional? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete("/me/professional");
      toast.success("Perfil excluído.");
      setForm({ name: "", sector: "", city: "", region: "", phone: "", whatsapp: "", contact_email: "", bio: "", services: [], photo_url: "" });
      setServicesText(""); setPhoto([]); setPmeta(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-6 sm:p-8 space-y-5 max-w-3xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-head text-xl font-semibold text-[#1c1917]">Meu Perfil Profissional</h2>
          <p className="text-sm text-[#78716c]">Estes dados aparecem no diretório público.</p>
        </div>
        {pmeta && (
          <span className="text-xs bg-[#e8f0ec] text-[#1b5e3f] px-3 py-1.5 rounded-full whitespace-nowrap">Perfil ativo</span>
        )}
      </div>

      {pmeta && user?.subscription?.status !== "active" && user?.role !== "admin" && (
        <div data-testid="profile-inactive-warning" className="flex items-start gap-2 text-sm bg-[#fef9ec] border border-[#fef3c7] text-[#92400e] rounded-xl px-4 py-3">
          <Clock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Seu perfil <strong>não aparece no diretório público</strong> porque sua assinatura está {user?.subscription?.status === "expired" ? "expirada" : "inativa"}. Ative na aba <strong>Assinatura</strong> para ser encontrado.</span>
        </div>
      )}

      {user?.subscription?.status !== "active" && user?.role !== "admin" && (
        <a href={withRef(STRIPE_PROMO_LINK, user)} target="_blank" rel="noreferrer" data-testid="profile-subscription-voucher" className="block">
          <div className="rounded-2xl border-2 border-dashed border-[#1b5e3f] bg-[#e8f0ec] p-5 flex items-center gap-4 hover:bg-[#dcebe2] transition-colors">
            <div className="w-12 h-12 rounded-xl bg-[#1b5e3f] flex items-center justify-center shrink-0">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-head font-semibold text-[#1c1917]">Ative seu cadastro no diretório — Promoção 10€/ano</p>
              <p className="text-sm text-[#1b5e3f]">Clique para pagar com segurança pelo Stripe e aparecer no diretório por 1 ano.</p>
            </div>
            <span className="ml-auto text-sm font-semibold text-[#1b5e3f] underline shrink-0">Pagar →</span>
          </div>
        </a>
      )}

      {user?.role !== "admin" && (
        <div data-testid="profile-advertiser-vouchers" className="grid sm:grid-cols-2 gap-3">
          <a href={withRef(STRIPE_ADVERTISER_LINKS.mensal, user)} target="_blank" rel="noreferrer" data-testid="profile-advertiser-voucher-mensal" className="block">
            <div className="h-full rounded-2xl border-2 border-dashed border-[#f59e0b] bg-[#fef9ec] p-5 flex items-center gap-3 hover:bg-[#fef3c7] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-[#f59e0b] flex items-center justify-center shrink-0"><Gift className="w-5 h-5 text-white" /></div>
              <div className="min-w-0">
                <p className="font-head font-semibold text-[#1c1917] text-sm">Anunciante — 150€/mês</p>
                <p className="text-xs text-[#92400e]">Anuncie no portal por 1 mês. Pagar →</p>
              </div>
            </div>
          </a>
          <a href={withRef(STRIPE_ADVERTISER_LINKS.anual, user)} target="_blank" rel="noreferrer" data-testid="profile-advertiser-voucher-anual" className="block">
            <div className="h-full rounded-2xl border-2 border-dashed border-[#f59e0b] bg-[#fef9ec] p-5 flex items-center gap-3 hover:bg-[#fef3c7] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-[#f59e0b] flex items-center justify-center shrink-0"><Gift className="w-5 h-5 text-white" /></div>
              <div className="min-w-0">
                <p className="font-head font-semibold text-[#1c1917] text-sm">Anunciante — 1200€/ano</p>
                <p className="text-xs text-[#92400e]">Anunciante oficial por 1 ano. Pagar →</p>
              </div>
            </div>
          </a>
        </div>
      )}

      {pmeta && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#78716c] bg-[#fafaf8] border border-[#e7e5e4] rounded-xl px-4 py-3">
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Criado em: <strong className="text-[#1c1917]">{fmtDate(pmeta.created_at)}</strong></span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Última atualização: <strong className="text-[#1c1917]">{fmtDate(pmeta.updated_at)}</strong></span>
          <span>Total de atualizações: <strong className="text-[#1c1917]">{pmeta.updates?.length || 0}</strong></span>
        </div>
      )}

      <FileUpload label="Foto de perfil" hint="JPG ou PNG" accept="image/*" category="photo" value={photo} onChange={setPhoto} testid="profile-photo-upload" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome / Empresa"><input data-testid="prof-name" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Setor / Ramo de atividade">
          <select data-testid="prof-sector" required className={inputCls} value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}>
            <option value="">Selecione</option>{meta.sectors?.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Cidade">
          <select data-testid="prof-city" required className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}>
            <option value="">Selecione</option>{meta.cities?.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Região">
          <select data-testid="prof-region" required className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
            <option value="">Selecione</option>{meta.regions?.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Telefone"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
        <Field label="WhatsApp"><input data-testid="prof-whatsapp" className={inputCls} placeholder="+351 ..." value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></Field>
        <Field label="E-mail de contato"><input className={inputCls} value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
      </div>
      <Field label="Serviços (separados por vírgula)"><input data-testid="prof-services" className={inputCls} placeholder="Ex: Pintura, Reformas, Elétrica" value={servicesText} onChange={(e) => setServicesText(e.target.value)} /></Field>
      <Field label="Sobre / Bio"><textarea data-testid="prof-bio" rows={4} className={inputCls} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></Field>
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="prof-save-btn" type="submit" disabled={loading} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {pmeta ? "Salvar alterações" : "Criar perfil"}</>}</Button>
        {pmeta && (
          <Button data-testid="prof-delete-btn" type="button" onClick={removeProfile} variant="outline" className="rounded-xl border-2 border-[#ef4444] text-[#ef4444] hover:bg-red-50"><Trash2 className="w-4 h-4 mr-2" /> Excluir perfil</Button>
        )}
      </div>
    </form>
  );
}

// ---------- Rentals Tab ----------
const STAY_OPTIONS = ["Sem permanência mínima", "1 mês", "3 meses", "6 meses", "1 ano", "Mais de 1 ano"];
const STRIPE_RENTAL_FEE_LINK = "https://buy.stripe.com/14AeVdc9x7Xy8gQ9yk6Vq05";

function RentalsTab({ meta }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const empty = { title: "", type: "", city: "", region: "", address: "", price: "", bedrooms: 0, bathrooms: 0, area: "", description: "" };
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [docs, setDocs] = useState([]);
  const [cpf, setCpf] = useState("");
  const [idDocument, setIdDocument] = useState("");
  const [contactPref, setContactPref] = useState("telefone_chat");
  const [seekingGender, setSeekingGender] = useState("tanto_faz");
  const [minStay, setMinStay] = useState("");
  const [residenceDoc, setResidenceDoc] = useState([]);
  const [contractDoc, setContractDoc] = useState([]);
  const [selfie, setSelfie] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.get("/me/rentals").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  // Stripe return polling (rental ad fee)
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || params.get("tab") !== "rentals") return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { toast.success("Pagamento confirmado! Seu imóvel está publicado."); params.delete("session_id"); setParams(params, { replace: true }); load(); return; }
        if (data.status === "expired") { toast.error("Pagamento expirado."); return; }
      } catch {}
      if (attempts < 6) setTimeout(poll, 2000); else toast.info("Verifique o status do imóvel em instantes.");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetDocs = () => { setCpf(""); setIdDocument(""); setContactPref("telefone_chat"); setSeekingGender("tanto_faz"); setMinStay(""); setResidenceDoc([]); setContractDoc([]); setSelfie([]); };

  const openCreate = () => {
    setEditId(null); setForm(empty); setPhotos([]); setDocs([]); resetDocs(); setShowForm(!showForm);
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({ title: r.title, type: r.type, city: r.city, region: r.region, address: r.address || "", price: r.price, bedrooms: r.bedrooms, bathrooms: r.bathrooms, area: r.area || "", description: r.description || "" });
    setPhotos((r.photos || []).map((u) => ({ url: u, filename: "foto", category: "photo" })));
    setDocs((r.documents || []).map((d) => ({ ...d, category: d.category || "document", filename: d.filename || "documento" })));
    setCpf(r.cpf || ""); setIdDocument(r.id_document || "");
    setContactPref(r.contact_preference || "telefone_chat");
    setSeekingGender(r.seeking_gender || "tanto_faz"); setMinStay(r.min_stay || "");
    setResidenceDoc(r.residence_doc ? [r.residence_doc] : []);
    setContractDoc(r.contract_doc ? [r.contract_doc] : []);
    setSelfie(r.selfie ? [r.selfie] : []);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const docOf = (arr) => (arr[0] ? { id: arr[0].id, category: arr[0].category, filename: arr[0].filename, url: arr[0].url } : null);
    if (!editId) {
      if (!cpf.trim()) { toast.error("Informe o número do CPF do Brasil."); return; }
      if (!isValidCPF(cpf)) { toast.error("CPF inválido. Verifique os números digitados."); return; }
      if (!residenceDoc[0]) { toast.error("Anexe a autorização de residência ou cadastro na ordem dos mediadores."); return; }
      if (!contractDoc[0]) { toast.error("Anexe o contrato de arrendamento ou declaração do imóvel."); return; }
      if (!selfie[0]) { toast.error("Anexe a selfie de verificação."); return; }
    }
    if (editId && cpf.trim() && !isValidCPF(cpf)) { toast.error("CPF inválido. Verifique os números digitados."); return; }
    setLoading(true);
    try {
      const payload = {
        ...form, price: parseFloat(form.price) || 0, bedrooms: parseInt(form.bedrooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0, area: parseFloat(form.area) || 0,
        photos: photos.map((p) => p.url),
        documents: docs.map((d) => ({ id: d.id, category: d.category, filename: d.filename, url: d.url })),
        cpf: cpf.trim(), id_document: idDocument.trim(),
        contact_preference: contactPref, seeking_gender: seekingGender, min_stay: minStay,
        residence_doc: docOf(residenceDoc),
        contract_doc: docOf(contractDoc),
        selfie: docOf(selfie),
        origin_url: window.location.origin,
      };
      if (editId) {
        await api.put(`/me/rentals/${editId}`, payload);
        toast.success("Imóvel atualizado!");
        setForm(empty); setPhotos([]); setDocs([]); resetDocs(); setShowForm(false); setEditId(null); load();
      } else {
        const { data } = await api.post("/me/rentals", payload);
        setForm(empty); setPhotos([]); setDocs([]); resetDocs(); setShowForm(false); setEditId(null); load();
        if (data.requires_payment) {
          toast.info("Imóvel criado! Pague a taxa de 10€ no botão do anúncio para publicá-lo.");
        } else {
          toast.success("1º imóvel publicado gratuitamente!");
        }
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este anúncio de imóvel?")) return;
    await api.delete(`/me/rentals/${id}`); toast.success("Imóvel removido."); load();
  };

  const activeCount = items.filter((r) => r.status === "active").length;
  const freeRentalId = [...items].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0]?.id;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-head text-xl font-semibold text-[#1c1917]">Meus Imóveis</h2>
        <Button data-testid="add-rental-btn" onClick={openCreate} className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706]"><Plus className="w-4 h-4 mr-2" /> {showForm && !editId ? "Fechar" : "Cadastrar imóvel"}</Button>
      </div>

      <div data-testid="rentals-info-banner" className="rounded-2xl border border-[#1b5e3f]/20 bg-[#e8f0ec] p-5 space-y-2">
        <p className="font-head font-semibold text-[#1c1917]">O teu 1.º anúncio é grátis 🎉</p>
        <p className="text-sm text-[#1b5e3f]">A partir do 2.º imóvel, cada anúncio tem o valor de <strong>10€</strong>. Tens acesso a uma área privada onde podes gerir o teu anúncio e os contactos que receberes. Podes resolver questões, trocar informações e organizar visitas de forma eficiente através do nosso chat.</p>
        {activeCount >= 1 && !editId && <p className="text-xs text-[#92400e]">Já tens {activeCount} imóvel(is) ativo(s) — o próximo anúncio terá a taxa de 10€.</p>}
      </div>

      <a
        href={`${STRIPE_RENTAL_FEE_LINK}?client_reference_id=${encodeURIComponent("rental:" + (user?.id || ""))}`}
        target="_blank"
        rel="noreferrer"
        data-testid="rentals-fee-voucher"
        className="block"
      >
        <div className="rounded-2xl border-2 border-dashed border-[#f59e0b] bg-[#fef9ec] p-5 flex items-center gap-4 hover:bg-[#fef3c7] transition-colors">
          <div className="w-12 h-12 rounded-xl bg-[#f59e0b] flex items-center justify-center shrink-0">
            <Gift className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-head font-semibold text-[#1c1917]">Voucher de pagamento — Anúncio de imóvel (10€)</p>
            <p className="text-sm text-[#92400e]">Clique para pagar a taxa de 10€ do anúncio com segurança pelo Stripe.</p>
          </div>
          <span className="ml-auto text-sm font-semibold text-[#1b5e3f] underline shrink-0">Pagar →</span>
        </div>
      </a>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-6 sm:p-8 space-y-5">
          <h3 className="font-head font-semibold text-[#1c1917]">{editId ? "Editar imóvel" : "Detalhes do imóvel"}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Título"><input data-testid="rental-title-input" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <Field label="Tipo"><select data-testid="rental-type-input" required className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="">Selecione</option>{meta.rental_types?.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
            <Field label="Cidade"><select required className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}><option value="">Selecione</option>{meta.cities?.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
            <Field label="Região"><select required className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}><option value="">Selecione</option>{meta.regions?.map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
            <Field label="Endereço"><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Preço do imóvel (euros/mês)"><input data-testid="rental-price-input" type="number" required className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
            <Field label="Quartos"><input type="number" className={inputCls} value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} /></Field>
            <Field label="Casas de banho"><input type="number" className={inputCls} value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} /></Field>
            <Field label="Área (m²)"><input type="number" className={inputCls} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></Field>
          </div>
          <Field label="Descrição"><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <FileUpload label="Fotos do imóvel" hint="Várias fotos (JPG/PNG)" accept="image/*" category="photo" multiple value={photos} onChange={setPhotos} testid="rental-photos-upload" />

          {/* Contact preference */}
          <div className="border border-[#e7e5e4] rounded-2xl p-5 space-y-3">
            <p className="font-head font-semibold text-[#1c1917]">Como preferes ser contactado?</p>
            {[
              { v: "telefone_chat", t: "Telefone e mensagens no nosso chat (recomendado)", s: "Irás receber um aviso das mensagens por e-mail e notificações na nossa app" },
              { v: "chat", t: "Só por mensagens de chat", s: "Irás receber um aviso das mensagens por e-mail e notificações na nossa app" },
              { v: "telefone", t: "Só por telefone", s: "" },
            ].map((o) => (
              <label key={o.v} data-testid={`rental-contact-${o.v}`} className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${contactPref === o.v ? "border-[#1b5e3f] bg-[#e8f0ec]" : "border-[#e7e5e4] hover:bg-[#fafaf8]"}`}>
                <input type="radio" name="contactPref" className="mt-1 accent-[#1b5e3f]" checked={contactPref === o.v} onChange={() => setContactPref(o.v)} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[#1c1917]">{o.t}</span>
                  {o.s && <span className="block text-xs text-[#78716c] mt-0.5">{o.s}</span>}
                </span>
              </label>
            ))}
          </div>

          <div className="bg-[#fef9ec] border border-[#fef3c7] rounded-2xl p-5 space-y-4">
            <div>
              <p className="font-head font-semibold text-[#1c1917]">Verificação obrigatória</p>
              <p className="text-xs text-[#92400e]">Documentos privados — visíveis apenas para você e o administrador.</p>
            </div>
            <Field label="Número do CPF (Brasil) *">
              <input data-testid="rental-cpf-input" required={!editId} className={inputCls} placeholder="000.000.000-00" value={cpf} onChange={(e) => setCpf(formatCPF(e.target.value))} />
            </Field>
            <Field label="Número do Cartão de Cidadão ou Autorização de Residência">
              <input data-testid="rental-iddoc-input" className={inputCls} placeholder="Ex: nº do título de residência" value={idDocument} onChange={(e) => setIdDocument(e.target.value)} />
            </Field>
            <FileUpload label="Autorização de residência ou cadastro na ordem dos mediadores imobiliários *" hint="Foto ou PDF (privado)" accept="image/*,application/pdf" visibility="private" category="residencia" value={residenceDoc} onChange={setResidenceDoc} testid="rental-residence-upload" />
            <FileUpload label="Contrato de arrendamento ou declaração do imóvel *" hint="Foto ou PDF (privado)" accept="image/*,application/pdf" visibility="private" category="contrato" value={contractDoc} onChange={setContractDoc} testid="rental-contract-upload" />
            <FileUpload label="Selfie de verificação *" hint="Foto sua segurando o documento (privado)" accept="image/*" visibility="private" category="selfie" value={selfie} onChange={setSelfie} testid="rental-selfie-upload" />
          </div>

          {/* Tenant preferences */}
          <div className="border border-[#e7e5e4] rounded-2xl p-5 space-y-4">
            <p className="font-head font-semibold text-[#1c1917]">Sobre a pessoa que procuras</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Género (opcional)">
                <select data-testid="rental-gender-input" className={inputCls} value={seekingGender} onChange={(e) => setSeekingGender(e.target.value)}>
                  <option value="tanto_faz">Tanto faz</option>
                  <option value="rapaz">Rapaz</option>
                  <option value="rapariga">Rapariga</option>
                </select>
              </Field>
              <Field label="Permanência mínima">
                <select data-testid="rental-minstay-input" className={inputCls} value={minStay} onChange={(e) => setMinStay(e.target.value)}>
                  <option value="">Seleciona</option>
                  {STAY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div className="border border-[#e7e5e4] rounded-2xl p-4">
            <p className="text-sm font-medium text-[#1c1917] mb-3">Outros documentos (opcional)</p>
            <FileUpload label="Documentos adicionais" hint="Foto ou PDF (privado)" accept="image/*,application/pdf" visibility="private" category="document" multiple value={docs} onChange={setDocs} testid="rental-docs-upload" />
          </div>

          {/* Tips */}
          <div data-testid="rental-tips-box" className="rounded-2xl bg-[#e8f0ec] border border-[#1b5e3f]/20 p-5">
            <p className="font-head font-semibold text-[#1c1917] mb-1">Lembra-te que...</p>
            <ul className="text-sm text-[#1b5e3f] space-y-1 list-disc pl-5">
              <li>Fotos, plantas e vídeos atraem mais pessoas para o teu anúncio.</li>
              <li>Se tiveres uma planta do imóvel, podes tirar uma foto da mesma ou desenhá-la à mão e tirar uma fotografia do desenho.</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Button data-testid="rental-submit-btn" type="submit" disabled={loading} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Salvar alterações" : (activeCount >= 1 ? "Publicar imóvel (10€)" : "Publicar 1º imóvel (grátis)"))}</Button>
            {editId && <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditId(null); setForm(empty); setPhotos([]); setDocs([]); resetDocs(); }} className="rounded-xl">Cancelar</Button>}
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.length === 0 && !showForm && <p className="text-[#78716c] text-sm">Você ainda não cadastrou imóveis.</p>}
        {items.map((r) => (
          <div key={r.id} data-testid={`my-rental-${r.id}`} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
            <div className="aspect-[16/9] bg-[#e8f0ec]">{r.photos?.[0] && <img src={r.photos[0]} alt="" className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="font-head font-bold text-[#1b5e3f]">€{Number(r.price).toLocaleString("pt-PT")}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(r)} className="text-[#78716c] hover:text-[#1b5e3f] p-1" data-testid={`edit-rental-${r.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(r.id)} className="text-[#78716c] hover:text-[#ef4444] p-1" data-testid={`delete-rental-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="font-medium text-[#1c1917] mt-1">{r.title}</p>
              <p className="text-sm text-[#78716c]">{r.city}, {r.region}</p>
              <p data-testid={`rental-fee-${r.id}`} className="text-xs mt-1.5 inline-flex items-center gap-1 text-[#57534e]">
                Valor do anúncio: {r.id === freeRentalId ? (
                  <strong className="text-[#1b5e3f]">Grátis (1.º anúncio)</strong>
                ) : (
                  <strong className="text-[#d97706]">10€</strong>
                )}
              </p>
              <div className="mt-2">
                {r.status === "active" ? (
                  <span className="text-xs bg-[#e8f0ec] text-[#1b5e3f] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Publicado</span>
                ) : (
                  <span className="text-xs bg-[#fef3c7] text-[#d97706] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Aguardando pagamento (10€)</span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#78716c] mt-2 pt-2 border-t border-[#e7e5e4]">
                <span>Criado: <strong className="text-[#1c1917]">{fmtDate(r.created_at)}</strong></span>
                <span>Atualizado: <strong className="text-[#1c1917]">{fmtDate(r.updated_at)}</strong></span>
                <span>Atualizações: <strong className="text-[#1c1917]">{r.updates?.length || 1}</strong></span>
              </div>
              {r.documents?.length > 0 && <p className="text-xs text-[#10b981] mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {r.documents.length} documento(s) anexado(s)</p>}
              <div className="mt-3 flex flex-col gap-2">
                <button onClick={() => openEdit(r)} data-testid={`edit-rental-btn-${r.id}`} className="flex items-center justify-center gap-2 w-full rounded-xl border border-[#1b5e3f] text-[#1b5e3f] text-sm font-semibold py-2.5 hover:bg-[#e8f0ec] transition-colors"><Pencil className="w-4 h-4" /> Editar informações</button>
                {r.status !== "active" && (
                  <a
                    href={`${STRIPE_RENTAL_FEE_LINK}?client_reference_id=${encodeURIComponent("rental:" + (user?.id || ""))}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`pay-rental-btn-${r.id}`}
                    className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#1b5e3f] text-white text-sm font-semibold py-2.5 hover:bg-[#13422c] transition-colors"
                  ><Gift className="w-4 h-4" /> Pagar anúncio (10€)</a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Job Offers Tab ----------
function JobOffersTab({ meta }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const empty = { company_name: "", nif: "", address: "", website: "", title: "", description: "", salary: "", city: "", region: "", sector: "", contact_email: "", phone: "" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const subActive = user?.subscription?.status === "active";

  const load = useCallback(() => api.get("/me/job-offers").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (j) => {
    setEditId(j.id);
    setForm({ company_name: j.company_name, nif: j.nif, address: j.address || "", website: j.website || "", title: j.title, description: j.description || "", salary: j.salary || "", city: j.city, region: j.region, sector: j.sector, contact_email: j.contact_email || "", phone: j.phone || "" });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) { await api.put(`/me/job-offers/${editId}`, form); toast.success("Oferta atualizada!"); }
      else { await api.post("/me/job-offers", form); toast.success("Oferta de trabalho publicada!"); }
      setForm(empty); setShowForm(false); setEditId(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir esta oferta de trabalho?")) return;
    await api.delete(`/me/job-offers/${id}`); toast.success("Oferta removida."); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-head text-xl font-semibold text-[#1c1917]">Ofertas de Trabalho</h2>
        <Button data-testid="add-job-btn" onClick={() => { setEditId(null); setForm(empty); setShowForm(!showForm); }} disabled={!subActive} className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706]"><Plus className="w-4 h-4 mr-2" /> {showForm && !editId ? "Fechar" : "Publicar vaga"}</Button>
      </div>

      {!subActive && (
        <div data-testid="jobs-inactive-warning" className="flex items-start gap-2 text-sm bg-[#fef9ec] border border-[#fef3c7] text-[#92400e] rounded-xl px-4 py-3">
          <Clock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Você precisa de uma <strong>assinatura ativa</strong> para publicar ofertas de trabalho. Ative na aba <strong>Assinatura</strong>.</span>
        </div>
      )}

      {showForm && subActive && (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-6 sm:p-8 space-y-5">
          <h3 className="font-head font-semibold text-[#1c1917]">{editId ? "Editar vaga" : "Dados da vaga"}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome da empresa"><input data-testid="job-company-input" required className={inputCls} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Field>
            <Field label="NIF ou NIPC"><input data-testid="job-nif-input" required className={inputCls} value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} /></Field>
            <Field label="Cargo / Vaga"><input data-testid="job-title-input" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <Field label="Valor da remuneração"><input data-testid="job-salary-input" className={inputCls} placeholder="Ex: 1000€/mês" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} /></Field>
            <Field label="Setor / Área"><select data-testid="job-sector-input" required className={inputCls} value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}><option value="">Selecione</option>{meta.sectors?.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
            <Field label="Cidade"><select required className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}><option value="">Selecione</option>{meta.cities?.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
            <Field label="Região"><select required className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}><option value="">Selecione</option>{meta.regions?.map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
            <Field label="Endereço"><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Site"><input data-testid="job-website-input" className={inputCls} placeholder="https://..." value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
            <Field label="E-mail de contato"><input type="email" className={inputCls} value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
            <Field label="Telefone de contato"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          </div>
          <Field label="Descrição da vaga"><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="flex gap-3">
            <Button data-testid="job-submit-btn" type="submit" disabled={loading} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Salvar alterações" : "Publicar vaga")}</Button>
            {editId && <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditId(null); setForm(empty); }} className="rounded-xl">Cancelar</Button>}
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.length === 0 && !showForm && <p className="text-[#78716c] text-sm">Você ainda não publicou ofertas de trabalho.</p>}
        {items.map((j) => (
          <div key={j.id} data-testid={`my-job-${j.id}`} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-head font-semibold text-[#1c1917]">{j.title}</p>
                <p className="text-sm text-[#1b5e3f]">{j.company_name}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(j)} className="text-[#78716c] hover:text-[#1b5e3f] p-1" data-testid={`edit-job-${j.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(j.id)} className="text-[#78716c] hover:text-[#ef4444] p-1" data-testid={`delete-job-${j.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-[#78716c] mt-1">{j.city}, {j.region} · {j.sector}{j.salary ? ` · ${j.salary}` : ""}</p>
            <p className="text-xs text-[#78716c] mt-1">NIF/NIPC: {j.nif}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------- Ads Tab ----------
function AdsTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", link_url: "", package: "basico" });
  const [image, setImage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.get("/me/ads").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const [editId, setEditId] = useState(null);
  const openCreate = () => { setEditId(null); setForm({ title: "", description: "", link_url: "", package: "basico" }); setImage([]); setShowForm(!showForm); };
  const openEdit = (a) => { setEditId(a.id); setForm({ title: a.title, description: a.description || "", link_url: a.link_url || "", package: a.package }); setImage(a.image_url ? [{ url: a.image_url, filename: "img", category: "photo" }] : []); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const saveEdit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.put(`/me/ads/${editId}`, { title: form.title, description: form.description, image_url: image[0]?.url || "", link_url: form.link_url });
      toast.success("Anúncio atualizado!"); setShowForm(false); setEditId(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const delAd = async (id) => { if (!window.confirm("Excluir este anúncio?")) return; await api.delete(`/me/ads/${id}`); toast.success("Anúncio removido."); load(); };

  // Stripe return polling
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { toast.success("Pagamento confirmado! Seu anúncio está ativo."); params.delete("session_id"); setParams(params, { replace: true }); load(); return; }
        if (data.status === "expired") { toast.error("Pagamento expirado."); return; }
      } catch {}
      if (attempts < 6) setTimeout(poll, 2000); else toast.info("Verifique o status do anúncio em instantes.");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, image_url: image[0]?.url || "", origin_url: window.location.origin };
      const { data } = await api.post("/ads/checkout", payload);
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  const statusBadge = (s) => {
    if (s === "active") return <span className="text-xs bg-[#e8f0ec] text-[#1b5e3f] px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Ativo</span>;
    return <span className="text-xs bg-[#fef3c7] text-[#d97706] px-2 py-0.5 rounded-full flex items-center gap-1"><Clock className="w-3 h-3" /> Aguardando pagamento</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-head text-xl font-semibold text-[#1c1917]">Meus Anúncios / Propagandas</h2>
          <a href="/propagandas" data-testid="ads-view-propagandas" className="text-sm text-[#1b5e3f] font-medium hover:underline">Ver página pública de Propagandas →</a>
        </div>
        <Button data-testid="add-ad-btn" onClick={openCreate} className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706]"><Plus className="w-4 h-4 mr-2" /> {showForm && !editId ? "Fechar" : "Criar anúncio"}</Button>
      </div>

      {showForm && (
        <form onSubmit={editId ? saveEdit : checkout} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-6 sm:p-8 space-y-5 max-w-2xl">
          {editId && <h3 className="font-head font-semibold text-[#1c1917]">Editar anúncio</h3>}
          <Field label="Título do anúncio"><input data-testid="ad-title-input" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Descrição"><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Link de destino (opcional)"><input className={inputCls} placeholder="https://..." value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} /></Field>
          <FileUpload label="Imagem do anúncio" hint="Banner (JPG/PNG)" accept="image/*" category="photo" value={image} onChange={setImage} testid="ad-image-upload" />
          {!editId && (
            <Field label="Plano">
              <div className="grid grid-cols-2 gap-3">
                {[{ id: "basico", label: "Básico", price: "€9,99", desc: "30 dias na listagem" }, { id: "premium", label: "Premium", price: "€29,99", desc: "30 dias em destaque" }].map((p) => (
                  <button type="button" key={p.id} data-testid={`ad-plan-${p.id}`} onClick={() => setForm({ ...form, package: p.id })} className={`text-left rounded-2xl border-2 p-4 transition-all ${form.package === p.id ? "border-[#1b5e3f] bg-[#e8f0ec]" : "border-[#e7e5e4] bg-white"}`}>
                    <div className="font-head font-bold text-[#1c1917]">{p.label}</div>
                    <div className="text-2xl font-head font-bold text-[#1b5e3f]">{p.price}</div>
                    <div className="text-xs text-[#78716c] mt-1">{p.desc}</div>
                  </button>
                ))}
              </div>
            </Field>
          )}
          <div className="flex gap-3">
            <Button data-testid="ad-checkout-btn" type="submit" disabled={loading} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Salvar alterações" : "Pagar e publicar")}</Button>
            {editId && <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }} className="rounded-xl">Cancelar</Button>}
          </div>
          {!editId && <p className="text-xs text-[#78716c]">Pagamento seguro via Stripe.</p>}
        </form>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.length === 0 && !showForm && <p className="text-[#78716c] text-sm">Você ainda não criou anúncios.</p>}
        {items.map((a) => (
          <div key={a.id} data-testid={`my-ad-${a.id}`} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
            {a.image_url && <img src={a.image_url} alt="" className="w-full h-28 object-cover" />}
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-[#1c1917]">{a.title}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(a)} className="text-[#78716c] hover:text-[#1b5e3f] p-1" data-testid={`edit-ad-${a.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delAd(a.id)} className="text-[#78716c] hover:text-[#ef4444] p-1" data-testid={`delete-ad-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-1">{statusBadge(a.status)}</div>
              <p className="text-sm text-[#78716c] line-clamp-2 mt-2">{a.description}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#78716c] mt-2 pt-2 border-t border-[#e7e5e4]">
                <span>Criado: <strong className="text-[#1c1917]">{fmtDate(a.created_at)}</strong></span>
                <span>Atualizado: <strong className="text-[#1c1917]">{fmtDate(a.updated_at)}</strong></span>
                <span>Atualizações: <strong className="text-[#1c1917]">{a.updates?.length || 1}</strong></span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Admin Tab ----------
function AdminTab() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pros, setPros] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [ads, setAds] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [redit, setRedit] = useState(null);
  const [rview, setRview] = useState(null);
  const docHref = (d) => (d?.url ? `${d.url}?auth=${localStorage.getItem("bpt_token") || ""}` : "#");
  const [aedit, setAedit] = useState(null);
  const [uview, setUview] = useState(null);
  const [udetail, setUdetail] = useState(null);
  const [uedit, setUedit] = useState(null);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/professionals").then((r) => setPros(r.data)).catch(() => {});
    api.get("/admin/rentals").then((r) => setRentals(r.data)).catch(() => {});
    api.get("/admin/ads").then((r) => setAds(r.data)).catch(() => {});
    api.get("/admin/voucher-payments").then((r) => setVouchers(r.data)).catch(() => {});
    api.get("/admin/job-offers").then((r) => setJobs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const delUser = async (u) => {
    if (!window.confirm(`Excluir o usuário ${u.name} (${u.email})? Isso remove perfil, imóveis e anúncios dele.`)) return;
    try { await api.delete(`/admin/users/${u.user_id}`); toast.success("Usuário excluído."); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delPro = async (p) => {
    if (!window.confirm(`Excluir o perfil de ${p.name}?`)) return;
    try { await api.delete(`/admin/professionals/${p.id}`); toast.success("Perfil excluído."); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delRental = async (r) => {
    if (!window.confirm(`Excluir o anúncio "${r.title}"?`)) return;
    try { await api.delete(`/admin/rentals/${r.id}`); toast.success("Anúncio excluído."); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const grantVoucher = async (p) => {
    if (!window.confirm(`Conceder assinatura ANUAL GRÁTIS (voucher) para ${p.name}? O perfil ficará ativo por 1 ano sem custo.`)) return;
    try {
      const { data } = await api.post(`/admin/grant-subscription`, { user_id: p.user_id, plan: "promo" });
      toast.success(`Voucher de assinatura concedido! Ativo até ${fmtDate(data.expires_at)}.`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const grantAdvVoucher = async (p) => {
    if (!window.confirm(`Conceder ANUNCIANTE OFICIAL ANUAL GRÁTIS (voucher) para ${p.name}? Ficará como anunciante por 1 ano sem custo.`)) return;
    try {
      const { data } = await api.post(`/admin/grant-advertiser`, { user_id: p.user_id, plan: "anual" });
      toast.success(`Voucher de anunciante concedido! Ativo até ${fmtDate(data.expires_at)}.`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const grantCourtesy = async (p) => {
    if (!window.confirm(`Conceder CORTESIA de 1 mês (30 dias) no diretório para ${p.name}? O perfil ficará ativo por 30 dias sem custo.`)) return;
    try {
      const { data } = await api.post(`/admin/grant-courtesy`, { user_id: p.user_id });
      toast.success(`Cortesia de 1 mês concedida! Ativa até ${fmtDate(data.expires_at)}.`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveRental = async () => {
    try {
      await api.put(`/admin/rentals/${redit.id}`, {
        title: redit.title, type: redit.type, city: redit.city, region: redit.region,
        address: redit.address || "", price: parseFloat(redit.price) || 0,
        bedrooms: parseInt(redit.bedrooms) || 0, bathrooms: parseInt(redit.bathrooms) || 0,
        area: parseFloat(redit.area) || 0, description: redit.description || "",
        photos: redit.photos || [], documents: redit.documents || [],
      });
      toast.success("Anúncio atualizado."); setRedit(null); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delAd = async (a) => {
    if (!window.confirm(`Excluir o anúncio "${a.title}"?`)) return;
    try { await api.delete(`/admin/ads/${a.id}`); toast.success("Anúncio excluído."); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const activateAd = async (a) => {
    if (!window.confirm(`Liberar o anúncio "${a.title}" com voucher? Ele ficará ativo e aparecerá em Propagandas em destaque e nos perfis.`)) return;
    try { await api.post(`/admin/ads/${a.id}/activate`); toast.success("Anúncio liberado e ativo!"); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveAd = async () => {
    try {
      await api.put(`/admin/ads/${aedit.id}`, {
        title: aedit.title, description: aedit.description || "",
        image_url: aedit.image_url || "", link_url: aedit.link_url || "",
      });
      toast.success("Anúncio atualizado."); setAedit(null); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openUserDetail = async (u) => {
    if (uview === u.user_id) { setUview(null); setUdetail(null); return; }
    setUview(u.user_id); setUdetail(null); setUedit(null);
    try { const { data } = await api.get(`/admin/users/${u.user_id}/detail`); setUdetail(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleVerify = async (u) => {
    const next = !u.verified;
    try {
      await api.post(`/admin/users/${u.user_id}/verify`, { verified: next });
      toast.success(next ? "Usuário verificado." : "Verificação removida.");
      loadAll();
      if (uview === u.user_id) { const { data } = await api.get(`/admin/users/${u.user_id}/detail`); setUdetail(data); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveUser = async () => {
    try {
      await api.put(`/admin/users/${uedit.user_id}`, { name: uedit.name, email: uedit.email, role: uedit.role });
      toast.success("Usuário atualizado."); setUedit(null); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delJobOffer = async (id) => {
    if (!window.confirm("Excluir esta oferta de trabalho?")) return;
    try { await api.delete(`/admin/job-offers/${id}`); toast.success("Oferta removida."); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const requestInfo = async (u) => {
    const message = window.prompt(`Quais informações deseja solicitar a ${u.name}? A mensagem aparecerá no painel do usuário.`);
    if (message === null) return;
    try {
      await api.post(`/admin/users/${u.user_id}/request-info`, { message });
      toast.success(message.trim() ? "Solicitação enviada ao usuário." : "Solicitação removida.");
      loadAll();
      if (uview === u.user_id) { const { data } = await api.get(`/admin/users/${u.user_id}/detail`); setUdetail(data); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <h2 className="font-head text-xl font-semibold text-[#1c1917]">Painel do Administrador</h2>
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[["Usuários", stats.users], ["Profissionais", stats.professionals], ["Imóveis", stats.rentals], ["Anúncios ativos", stats.ads_active], ["Assinantes ativos", stats.subscribers_active], ["Anunciantes ativos", stats.advertisers_active], ["Pagamentos por voucher", stats.voucher_payments]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-5">
              <div className="text-3xl font-head font-bold text-[#1b5e3f]">{v}</div>
              <div className="text-sm text-[#78716c]">{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Voucher payments tracking */}
      <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden" data-testid="admin-voucher-payments">
        <div className="px-6 py-4 border-b border-[#e7e5e4] font-head font-semibold flex items-center gap-2"><Gift className="w-4 h-4 text-[#1b5e3f]" /> Pagamentos por voucher ({vouchers.length})</div>
        {vouchers.length === 0 ? (
          <div className="px-6 py-8 text-sm text-[#78716c]">Nenhum pagamento por voucher registrado ainda. Os pagamentos feitos pelos links do Stripe aparecem aqui automaticamente após a configuração do webhook.</div>
        ) : (
          <div className="divide-y divide-[#e7e5e4]">
            {vouchers.map((v) => (
              <div key={v.id} data-testid={`admin-voucher-${v.id}`} className="px-6 py-4 flex flex-wrap items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="font-medium text-[#1c1917]">{v.name || "—"} <span className="text-[#1b5e3f] font-head font-bold ml-1">{v.currency === "EUR" ? "€" : ""}{Number(v.amount).toLocaleString("pt-PT")}</span> {v.plan && <span className="text-xs bg-[#e8f0ec] text-[#1b5e3f] px-2 py-0.5 rounded-full ml-1">{v.plan}</span>}</div>
                  <div className="text-[#78716c] break-all">{v.email || "—"}{v.phone ? ` · ${v.phone}` : ""}</div>
                  <div className="text-xs text-[#78716c] mt-1">Pago em {fmtDate(v.paid_at)}</div>
                </div>
                <div className="shrink-0">
                  {v.auto_activated ? (
                    <span className="text-xs px-3 py-1.5 rounded-full bg-[#e8f0ec] text-[#1b5e3f] inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Ativado automaticamente</span>
                  ) : (
                    <span className="text-xs px-3 py-1.5 rounded-full bg-[#fef3c7] text-[#d97706] inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Ativação manual pendente</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profiles management */}
      <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#e7e5e4] font-head font-semibold">Perfis criados ({pros.length})</div>
        {pros.length === 0 ? (
          <div className="px-6 py-8 text-sm text-[#78716c]">Nenhum perfil criado ainda.</div>
        ) : (
          <div className="divide-y divide-[#e7e5e4]">
            {pros.map((p) => (
              <div key={p.id} data-testid={`admin-pro-${p.id}`} className="px-6 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-[#1c1917]">{p.name} <span className="text-xs bg-[#e8f0ec] text-[#1b5e3f] px-2 py-0.5 rounded-full ml-1">{p.sector}</span></div>
                    <div className="text-sm text-[#78716c]">{p.city}, {p.region} · {p.account_email}</div>
                    <div className="flex flex-wrap gap-x-5 gap-y-0.5 text-xs text-[#78716c] mt-2">
                      <span>Criado: <strong className="text-[#1c1917]">{fmtDate(p.created_at)}</strong></span>
                      <span>Atualizado: <strong className="text-[#1c1917]">{fmtDate(p.updated_at)}</strong></span>
                      <span>Atualizações: <strong className="text-[#1c1917]">{p.updates?.length || 1}</strong></span>
                      {p.subscription?.status === "active" ? (
                        <span className="text-[#1b5e3f] inline-flex items-center gap-1"><Crown className="w-3 h-3" /> Assinatura ativa{p.subscription?.plan ? ` (${p.subscription.plan})` : ""}</span>
                      ) : (
                        <span className="text-[#d97706]">Sem assinatura ativa</span>
                      )}
                      {p.advertiser?.status === "active" && (
                        <span className="text-[#1b5e3f] inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> Anunciante ativo</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`admin-pro-edit-${p.id}`} onClick={() => setEdit(edit?.id === p.id ? null : { ...p, servicesText: (p.services || []).join(", ") })} className="text-xs px-3 py-1.5 rounded-lg border border-[#e7e5e4] text-[#1c1917] hover:bg-[#fafaf8] inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                    <button data-testid={`admin-pro-delete-${p.id}`} onClick={() => delPro(p)} className="text-xs px-3 py-1.5 rounded-lg border border-[#ef4444] text-[#ef4444] hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
                  </div>
                </div>

                {edit?.id === p.id && (
                  <div className="mt-4 bg-[#fafaf8] border border-[#e7e5e4] rounded-xl p-4 space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Nome" />
                      <input className={inputCls} value={edit.sector} onChange={(e) => setEdit({ ...edit, sector: e.target.value })} placeholder="Setor" />
                      <input className={inputCls} value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} placeholder="Cidade" />
                      <input className={inputCls} value={edit.region} onChange={(e) => setEdit({ ...edit, region: e.target.value })} placeholder="Região" />
                      <input className={inputCls} value={edit.whatsapp || ""} onChange={(e) => setEdit({ ...edit, whatsapp: e.target.value })} placeholder="WhatsApp" />
                      <input className={inputCls} value={edit.servicesText} onChange={(e) => setEdit({ ...edit, servicesText: e.target.value })} placeholder="Serviços (vírgula)" />
                    </div>
                    <textarea rows={2} className={inputCls} value={edit.bio || ""} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} placeholder="Bio" />
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid={`admin-pro-save-${p.id}`} onClick={async () => {
                        try {
                          await api.put(`/admin/professionals/${p.id}`, {
                            name: edit.name, sector: edit.sector, city: edit.city, region: edit.region,
                            phone: edit.phone || "", whatsapp: edit.whatsapp || "", contact_email: edit.contact_email || "",
                            bio: edit.bio || "", services: edit.servicesText.split(",").map((s) => s.trim()).filter(Boolean), photo_url: edit.photo_url || "",
                          });
                          toast.success("Perfil atualizado."); setEdit(null); loadAll();
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]"><Save className="w-4 h-4 mr-2" /> Salvar</Button>
                      <Button data-testid={`admin-pro-voucher-${p.id}`} type="button" onClick={() => grantVoucher(p)} className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706]"><Gift className="w-4 h-4 mr-2" /> Perfil grátis (voucher anual)</Button>
                      <Button data-testid={`admin-pro-courtesy-${p.id}`} type="button" onClick={() => grantCourtesy(p)} variant="outline" className="rounded-xl border-2 border-[#f59e0b] text-[#d97706] hover:bg-[#fef3c7]"><Gift className="w-4 h-4 mr-2" /> Cortesia 1 mês (30 dias)</Button>
                      <Button data-testid={`admin-pro-adv-voucher-${p.id}`} type="button" onClick={() => grantAdvVoucher(p)} variant="outline" className="rounded-xl border-2 border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec]"><Building2 className="w-4 h-4 mr-2" /> Anunciante grátis (voucher anual)</Button>
                      <Button onClick={() => setEdit(null)} variant="outline" className="rounded-xl">Cancelar</Button>
                    </div>
                    <p className="text-xs text-[#78716c]">Os vouchers concedem 1 ano gratuito: o de perfil deixa o cadastro ativo no diretório; o de anunciante torna o usuário anunciante oficial. A <strong>Cortesia de 1 mês</strong> deixa o cadastro ativo no diretório por 30 dias — todos sem cobrança.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rentals management */}
      <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#e7e5e4] font-head font-semibold">Imóveis cadastrados ({rentals.length})</div>
        {rentals.length === 0 ? (
          <div className="px-6 py-8 text-sm text-[#78716c]">Nenhum imóvel cadastrado ainda.</div>
        ) : (
          <div className="divide-y divide-[#e7e5e4]">
            {rentals.map((r) => (
              <div key={r.id} data-testid={`admin-rental-${r.id}`} className="px-6 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-[#1c1917]">{r.title} <span className="text-xs bg-[#fef3c7] text-[#d97706] px-2 py-0.5 rounded-full ml-1">{r.type}</span> <span className="text-[#1b5e3f] font-head font-bold ml-1">€{Number(r.price).toLocaleString("pt-PT")}</span></div>
                    <div className="text-sm text-[#78716c]">{r.city}, {r.region} · {r.account_name} ({r.account_email})</div>
                    <div className="flex flex-wrap gap-x-5 gap-y-0.5 text-xs text-[#78716c] mt-2">
                      <span>Usuário cadastrado em: <strong className="text-[#1c1917]">{fmtDate(r.account_created_at)}</strong></span>
                      <span>Anúncio criado: <strong className="text-[#1c1917]">{fmtDate(r.created_at)}</strong></span>
                      <span>Última atualização: <strong className="text-[#1c1917]">{fmtDate(r.updated_at)}</strong></span>
                      <span>Atualizações: <strong className="text-[#1c1917]">{r.updates?.length || 1}</strong></span>
                      <span>Status: <strong className={r.status === "active" ? "text-[#1b5e3f]" : "text-[#d97706]"}>{r.status === "active" ? "Publicado" : "Aguardando pagamento"}</strong></span>
                      {r.documents?.length > 0 && <span className="text-[#10b981]">{r.documents.length} doc(s)</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`admin-rental-docs-${r.id}`} onClick={() => setRview(rview === r.id ? null : r.id)} className="text-xs px-3 py-1.5 rounded-lg border border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec] inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {rview === r.id ? "Ocultar" : "Dados e documentos"}</button>
                    <button data-testid={`admin-rental-edit-${r.id}`} onClick={() => setRedit(redit?.id === r.id ? null : { ...r })} className="text-xs px-3 py-1.5 rounded-lg border border-[#e7e5e4] text-[#1c1917] hover:bg-[#fafaf8] inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                    <button data-testid={`admin-rental-delete-${r.id}`} onClick={() => delRental(r)} className="text-xs px-3 py-1.5 rounded-lg border border-[#ef4444] text-[#ef4444] hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
                  </div>
                </div>

                {rview === r.id && (
                  <div data-testid={`admin-rental-details-${r.id}`} className="mt-4 bg-[#fafaf8] border border-[#e7e5e4] rounded-xl p-4 grid sm:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1.5">
                      <p className="font-head font-semibold text-[#1c1917] mb-2">Dados do anunciante</p>
                      <p><span className="text-[#78716c]">Nome:</span> <strong className="text-[#1c1917]">{r.account_name || "—"}</strong></p>
                      <p><span className="text-[#78716c]">E-mail (conta):</span> <strong className="text-[#1c1917] break-all">{r.account_email || "—"}</strong></p>
                      {r.owner_contact_email && <p><span className="text-[#78716c]">E-mail (contato):</span> <strong className="text-[#1c1917] break-all">{r.owner_contact_email}</strong></p>}
                      <p><span className="text-[#78716c]">Telefone:</span> <strong className="text-[#1c1917]">{r.owner_phone || "—"}</strong></p>
                      <p><span className="text-[#78716c]">CPF:</span> <strong className="text-[#1c1917]">{r.cpf || "—"}</strong></p>
                      <p><span className="text-[#78716c]">Endereço:</span> <strong className="text-[#1c1917]">{r.address || "—"}</strong></p>
                    </div>
                    <div className="space-y-2">
                      <p className="font-head font-semibold text-[#1c1917] mb-2">Documentos de verificação</p>
                      {[["Autorização de residência / Ordem dos mediadores", r.residence_doc], ["Contrato / Declaração do imóvel", r.contract_doc], ["Selfie de verificação", r.selfie]].map(([label, d], i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="text-[#78716c]">{label}</span>
                          {d?.url ? (
                            <a data-testid={`admin-rental-doc-link-${r.id}-${i}`} href={docHref(d)} target="_blank" rel="noreferrer" className="text-[#1b5e3f] font-semibold underline shrink-0">Abrir</a>
                          ) : (
                            <span className="text-[#d97706] text-xs shrink-0">Não enviado</span>
                          )}
                        </div>
                      ))}
                      {r.documents?.length > 0 && (
                        <div className="pt-1">
                          <span className="text-[#78716c]">Outros documentos:</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {r.documents.map((d, i) => (
                              <a key={i} href={docHref(d)} target="_blank" rel="noreferrer" className="text-xs text-[#1b5e3f] underline">{d.filename || `doc ${i + 1}`}</a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {redit?.id === r.id && (
                  <div className="mt-4 bg-[#fafaf8] border border-[#e7e5e4] rounded-xl p-4 space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input className={inputCls} value={redit.title} onChange={(e) => setRedit({ ...redit, title: e.target.value })} placeholder="Título" />
                      <input className={inputCls} value={redit.type} onChange={(e) => setRedit({ ...redit, type: e.target.value })} placeholder="Tipo" />
                      <input className={inputCls} value={redit.city} onChange={(e) => setRedit({ ...redit, city: e.target.value })} placeholder="Cidade" />
                      <input className={inputCls} value={redit.region} onChange={(e) => setRedit({ ...redit, region: e.target.value })} placeholder="Região" />
                      <input className={inputCls} type="number" value={redit.price} onChange={(e) => setRedit({ ...redit, price: e.target.value })} placeholder="Preço" />
                      <input className={inputCls} value={redit.address || ""} onChange={(e) => setRedit({ ...redit, address: e.target.value })} placeholder="Endereço" />
                    </div>
                    <textarea rows={2} className={inputCls} value={redit.description || ""} onChange={(e) => setRedit({ ...redit, description: e.target.value })} placeholder="Descrição" />
                    <div className="flex gap-2">
                      <Button data-testid={`admin-rental-save-${r.id}`} onClick={saveRental} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]"><Save className="w-4 h-4 mr-2" /> Salvar</Button>
                      <Button onClick={() => setRedit(null)} variant="outline" className="rounded-xl">Cancelar</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ads management */}
      <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#e7e5e4] font-head font-semibold">Anúncios cadastrados ({ads.length})</div>
        {ads.length === 0 ? (
          <div className="px-6 py-8 text-sm text-[#78716c]">Nenhum anúncio cadastrado ainda.</div>
        ) : (
          <div className="divide-y divide-[#e7e5e4]">
            {ads.map((a) => (
              <div key={a.id} data-testid={`admin-ad-${a.id}`} className="px-6 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {a.image_url && <img src={a.image_url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-medium text-[#1c1917]">{a.title} <span className={`text-xs px-2 py-0.5 rounded-full ml-1 ${a.status === "active" ? "bg-[#e8f0ec] text-[#1b5e3f]" : "bg-[#fef3c7] text-[#d97706]"}`}>{a.status === "active" ? "Ativo" : "Aguardando pagamento"}</span></div>
                      <div className="text-sm text-[#78716c]">{a.account_name} ({a.account_email}) · plano {a.package}</div>
                      <div className="flex flex-wrap gap-x-5 gap-y-0.5 text-xs text-[#78716c] mt-2">
                        <span>Criado: <strong className="text-[#1c1917]">{fmtDate(a.created_at)}</strong></span>
                        <span>Atualizado: <strong className="text-[#1c1917]">{fmtDate(a.updated_at)}</strong></span>
                        <span>Atualizações: <strong className="text-[#1c1917]">{a.updates?.length || 1}</strong></span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.status !== "active" && (
                      <button data-testid={`admin-ad-activate-${a.id}`} onClick={() => activateAd(a)} className="text-xs px-3 py-1.5 rounded-lg bg-[#1b5e3f] text-white hover:bg-[#13422c] inline-flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Liberar (voucher)</button>
                    )}
                    <button data-testid={`admin-ad-edit-${a.id}`} onClick={() => setAedit(aedit?.id === a.id ? null : { ...a })} className="text-xs px-3 py-1.5 rounded-lg border border-[#e7e5e4] text-[#1c1917] hover:bg-[#fafaf8] inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                    <button data-testid={`admin-ad-delete-${a.id}`} onClick={() => delAd(a)} className="text-xs px-3 py-1.5 rounded-lg border border-[#ef4444] text-[#ef4444] hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
                  </div>
                </div>

                {aedit?.id === a.id && (
                  <div className="mt-4 bg-[#fafaf8] border border-[#e7e5e4] rounded-xl p-4 space-y-3">
                    <input className={inputCls} value={aedit.title} onChange={(e) => setAedit({ ...aedit, title: e.target.value })} placeholder="Título" />
                    <textarea rows={2} className={inputCls} value={aedit.description || ""} onChange={(e) => setAedit({ ...aedit, description: e.target.value })} placeholder="Descrição" />
                    <input className={inputCls} value={aedit.link_url || ""} onChange={(e) => setAedit({ ...aedit, link_url: e.target.value })} placeholder="Link de destino" />
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid={`admin-ad-save-${a.id}`} onClick={saveAd} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]"><Save className="w-4 h-4 mr-2" /> Salvar</Button>
                      <Button data-testid={`admin-ad-voucher-${a.id}`} type="button" onClick={() => grantVoucher({ user_id: a.user_id, name: a.account_name || a.title })} className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706]"><Gift className="w-4 h-4 mr-2" /> Perfil grátis (voucher anual)</Button>
                      <Button data-testid={`admin-ad-courtesy-${a.id}`} type="button" onClick={() => grantCourtesy({ user_id: a.user_id, name: a.account_name || a.title })} variant="outline" className="rounded-xl border-2 border-[#f59e0b] text-[#d97706] hover:bg-[#fef3c7]"><Gift className="w-4 h-4 mr-2" /> Cortesia 1 mês (30 dias)</Button>
                      <Button data-testid={`admin-ad-adv-voucher-${a.id}`} type="button" onClick={() => grantAdvVoucher({ user_id: a.user_id, name: a.account_name || a.title })} variant="outline" className="rounded-xl border-2 border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec]"><Building2 className="w-4 h-4 mr-2" /> Anunciante grátis (voucher anual)</Button>
                      <Button onClick={() => setAedit(null)} variant="outline" className="rounded-xl">Cancelar</Button>
                    </div>
                    <p className="text-xs text-[#78716c]">Os vouchers concedem 1 ano gratuito ao dono do anúncio: assinatura (perfil ativo no diretório) e/ou anunciante oficial — sem cobrança.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Job offers management */}
      <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden" data-testid="admin-job-offers">
        <div className="px-6 py-4 border-b border-[#e7e5e4] font-head font-semibold flex items-center gap-2"><Briefcase className="w-4 h-4 text-[#1b5e3f]" /> Ofertas de trabalho ({jobs.length})</div>
        {jobs.length === 0 ? (
          <div className="px-6 py-8 text-sm text-[#78716c]">Nenhuma oferta de trabalho cadastrada.</div>
        ) : (
          <div className="divide-y divide-[#e7e5e4]">
            {jobs.map((j) => {
              const owner = { user_id: j.user_id, name: j.owner_name, email: j.owner_email, role: j.owner_role, verified: j.owner_verified };
              return (
                <div key={j.id} data-testid={`admin-job-${j.id}`} className="px-6 py-4 text-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[#1c1917]">{j.title}</span>
                        <span className="text-[#78716c]">· {j.company_name}</span>
                        {j.owner_verified && <span className="text-xs px-2 py-0.5 rounded-full bg-[#e8f0ec] text-[#1b5e3f] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Verificado</span>}
                      </div>
                      <div className="text-xs text-[#78716c] mt-0.5">{j.city}, {j.region} · {j.sector}{j.salary ? ` · ${j.salary}` : ""} · NIF/NIPC: {j.nif}</div>
                      <div className="text-xs text-[#78716c] mt-0.5">Cadastrado por: <strong className="text-[#1c1917]">{j.owner_name || "—"}</strong> ({j.owner_email || "—"})</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      <button data-testid={`admin-job-detail-${j.id}`} onClick={() => openUserDetail(owner)} className="text-xs px-3 py-1.5 rounded-lg border border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec] inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> Ver perfil</button>
                      {j.owner_role !== "admin" && (
                        <>
                          <button data-testid={`admin-job-verify-${j.id}`} onClick={() => toggleVerify(owner)} className={`text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1 ${j.owner_verified ? "border border-[#e7e5e4] text-[#78716c] hover:bg-[#fafaf8]" : "bg-[#1b5e3f] text-white hover:bg-[#13422c]"}`}><CheckCircle2 className="w-3.5 h-3.5" /> {j.owner_verified ? "Remover verificação" : "Verificar"}</button>
                          <button data-testid={`admin-job-edit-owner-${j.id}`} onClick={() => setUedit({ user_id: owner.user_id, name: owner.name, email: owner.email, role: owner.role })} className="text-xs px-3 py-1.5 rounded-lg border border-[#e7e5e4] text-[#1c1917] hover:bg-[#fafaf8] inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Editar perfil</button>
                          <button data-testid={`admin-job-request-info-${j.id}`} onClick={() => requestInfo(owner)} className="text-xs px-3 py-1.5 rounded-lg border border-[#f59e0b] text-[#d97706] hover:bg-[#fef9ec] inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Solicitar informações</button>
                        </>
                      )}
                      <button data-testid={`admin-job-delete-${j.id}`} onClick={() => delJobOffer(j.id)} className="text-xs px-3 py-1.5 rounded-lg border border-[#ef4444] text-[#ef4444] hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Excluir vaga</button>
                    </div>
                  </div>
                  {uedit?.user_id === owner.user_id && (
                    <div className="mt-3 bg-[#fafaf8] border border-[#e7e5e4] rounded-xl p-4 space-y-3">
                      <div className="grid sm:grid-cols-3 gap-3">
                        <input className={inputCls} value={uedit.name} onChange={(e) => setUedit({ ...uedit, name: e.target.value })} placeholder="Nome" />
                        <input className={inputCls} value={uedit.email} onChange={(e) => setUedit({ ...uedit, email: e.target.value })} placeholder="E-mail" />
                        <select className={inputCls} value={uedit.role} onChange={(e) => setUedit({ ...uedit, role: e.target.value })}><option value="user">user</option><option value="admin">admin</option></select>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={saveUser} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]"><Save className="w-4 h-4 mr-2" /> Salvar</Button>
                        <Button onClick={() => setUedit(null)} variant="outline" className="rounded-xl">Cancelar</Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Users management */}
      <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#e7e5e4] font-head font-semibold">Usuários cadastrados ({users.length})</div>
        <div className="divide-y divide-[#e7e5e4]">
          {users.map((u) => (
            <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="px-6 py-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#1c1917]">{u.name}</span>
                    <span className="text-[#78716c]">· {u.email}</span>
                    {u.verified ? (
                      <span data-testid={`admin-user-verified-${u.user_id}`} className="text-xs px-2 py-0.5 rounded-full bg-[#e8f0ec] text-[#1b5e3f] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Verificado</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#f5f5f4] text-[#78716c]">Não verificado</span>
                    )}
                    {u.info_request?.message && <span className="text-xs px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#d97706]">Info solicitada</span>}
                  </div>
                  <div className="text-xs text-[#78716c] mt-0.5">Cadastrado em {fmtDate(u.created_at)} · via {u.auth_provider}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#e8f0ec] text-[#1b5e3f]">{u.role}</span>
                  <button data-testid={`admin-user-detail-${u.user_id}`} onClick={() => openUserDetail(u)} className="text-xs px-3 py-1.5 rounded-lg border border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec] inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {uview === u.user_id ? "Ocultar" : "Ver perfil"}</button>
                  {u.role !== "admin" && (
                    <>
                      <button data-testid={`admin-user-verify-${u.user_id}`} onClick={() => toggleVerify(u)} className={`text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1 ${u.verified ? "border border-[#e7e5e4] text-[#78716c] hover:bg-[#fafaf8]" : "bg-[#1b5e3f] text-white hover:bg-[#13422c]"}`}><CheckCircle2 className="w-3.5 h-3.5" /> {u.verified ? "Remover verificação" : "Verificar"}</button>
                      <button data-testid={`admin-user-edit-${u.user_id}`} onClick={() => { setUedit(uedit?.user_id === u.user_id ? null : { user_id: u.user_id, name: u.name, email: u.email, role: u.role }); }} className="text-xs px-3 py-1.5 rounded-lg border border-[#e7e5e4] text-[#1c1917] hover:bg-[#fafaf8] inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                      <button data-testid={`admin-user-request-info-${u.user_id}`} onClick={() => requestInfo(u)} className="text-xs px-3 py-1.5 rounded-lg border border-[#f59e0b] text-[#d97706] hover:bg-[#fef9ec] inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Solicitar informações</button>
                      <button data-testid={`admin-user-delete-${u.user_id}`} onClick={() => delUser(u)} className="text-xs px-3 py-1.5 rounded-lg border border-[#ef4444] text-[#ef4444] hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
                    </>
                  )}
                </div>
              </div>

              {uedit?.user_id === u.user_id && (
                <div className="mt-3 bg-[#fafaf8] border border-[#e7e5e4] rounded-xl p-4 space-y-3">
                  <div className="grid sm:grid-cols-3 gap-3">
                    <input data-testid={`admin-user-edit-name-${u.user_id}`} className={inputCls} value={uedit.name} onChange={(e) => setUedit({ ...uedit, name: e.target.value })} placeholder="Nome" />
                    <input data-testid={`admin-user-edit-email-${u.user_id}`} className={inputCls} value={uedit.email} onChange={(e) => setUedit({ ...uedit, email: e.target.value })} placeholder="E-mail" />
                    <select data-testid={`admin-user-edit-role-${u.user_id}`} className={inputCls} value={uedit.role} onChange={(e) => setUedit({ ...uedit, role: e.target.value })}><option value="user">user</option><option value="admin">admin</option></select>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid={`admin-user-save-${u.user_id}`} onClick={saveUser} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]"><Save className="w-4 h-4 mr-2" /> Salvar</Button>
                    <Button onClick={() => setUedit(null)} variant="outline" className="rounded-xl">Cancelar</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-[#e7e5e4]">
                    <Button data-testid={`admin-user-courtesy-${u.user_id}`} type="button" onClick={() => grantCourtesy({ user_id: u.user_id, name: u.name })} variant="outline" className="rounded-xl border-2 border-[#f59e0b] text-[#d97706] hover:bg-[#fef3c7]"><Gift className="w-4 h-4 mr-2" /> Cortesia 1 mês (30 dias)</Button>
                    <Button data-testid={`admin-user-voucher-${u.user_id}`} type="button" onClick={() => grantVoucher({ user_id: u.user_id, name: u.name })} className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706]"><Gift className="w-4 h-4 mr-2" /> Perfil grátis (voucher anual)</Button>
                    <Button data-testid={`admin-user-adv-voucher-${u.user_id}`} type="button" onClick={() => grantAdvVoucher({ user_id: u.user_id, name: u.name })} variant="outline" className="rounded-xl border-2 border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec]"><Building2 className="w-4 h-4 mr-2" /> Anunciante grátis (voucher anual)</Button>
                  </div>
                  <p className="text-xs text-[#78716c]">Cortesia: 30 dias grátis no diretório. Voucher de perfil: 1 ano grátis no diretório. Voucher de anunciante: 1 ano como anunciante oficial.</p>
                </div>
              )}

              {uview === u.user_id && (
                <div data-testid={`admin-user-detail-panel-${u.user_id}`} className="mt-3 bg-[#fafaf8] border border-[#e7e5e4] rounded-xl p-4 space-y-4">
                  {!udetail ? (
                    <div className="text-sm text-[#78716c] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando...</div>
                  ) : (
                    <>
                      {/* Account data */}
                      <div>
                        <p className="font-head font-semibold text-[#1c1917] mb-1">Dados da conta</p>
                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                          <p><span className="text-[#78716c]">Nome:</span> <strong className="text-[#1c1917]">{udetail.user.name}</strong></p>
                          <p><span className="text-[#78716c]">E-mail:</span> <strong className="text-[#1c1917] break-all">{udetail.user.email}</strong></p>
                          <p><span className="text-[#78716c]">Função:</span> <strong className="text-[#1c1917]">{udetail.user.role}</strong></p>
                          <p><span className="text-[#78716c]">Acesso via:</span> <strong className="text-[#1c1917]">{udetail.user.auth_provider}</strong></p>
                          <p><span className="text-[#78716c]">Cadastrado em:</span> <strong className="text-[#1c1917]">{fmtDate(udetail.user.created_at)}</strong></p>
                          <p><span className="text-[#78716c]">Verificado:</span> <strong className={udetail.user.verified ? "text-[#1b5e3f]" : "text-[#d97706]"}>{udetail.user.verified ? `Sim (${fmtDate(udetail.user.verified_at)})` : "Não"}</strong></p>
                          <p><span className="text-[#78716c]">Assinatura:</span> <strong className="text-[#1c1917]">{udetail.user.subscription?.status === "active" ? `Ativa (${udetail.user.subscription.plan || "—"}) até ${fmtDate(udetail.user.subscription.expires_at)}` : "Inativa"}</strong></p>
                          <p><span className="text-[#78716c]">Anunciante:</span> <strong className="text-[#1c1917]">{udetail.user.advertiser?.status === "active" ? `Ativo (${udetail.user.advertiser.plan || "—"}) até ${fmtDate(udetail.user.advertiser.expires_at)}` : "Não"}</strong></p>
                          {udetail.user.info_request?.message && <p className="sm:col-span-2"><span className="text-[#78716c]">Informação solicitada:</span> <strong className="text-[#d97706]">{udetail.user.info_request.message}</strong></p>}
                        </div>
                      </div>

                      {/* Professional profile */}
                      <div>
                        <p className="font-head font-semibold text-[#1c1917] mb-1">Perfil profissional</p>
                        {udetail.professional ? (
                          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                            <p><span className="text-[#78716c]">Nome/Empresa:</span> <strong className="text-[#1c1917]">{udetail.professional.name}</strong></p>
                            <p><span className="text-[#78716c]">Setor:</span> <strong className="text-[#1c1917]">{udetail.professional.sector}</strong></p>
                            <p><span className="text-[#78716c]">Local:</span> <strong className="text-[#1c1917]">{udetail.professional.city}, {udetail.professional.region}</strong></p>
                            <p><span className="text-[#78716c]">Telefone:</span> <strong className="text-[#1c1917]">{udetail.professional.phone || "—"}</strong></p>
                            <p><span className="text-[#78716c]">WhatsApp:</span> <strong className="text-[#1c1917]">{udetail.professional.whatsapp || "—"}</strong></p>
                            <p><span className="text-[#78716c]">E-mail de contato:</span> <strong className="text-[#1c1917] break-all">{udetail.professional.contact_email || "—"}</strong></p>
                            {udetail.professional.services?.length > 0 && <p className="sm:col-span-2"><span className="text-[#78716c]">Serviços:</span> <strong className="text-[#1c1917]">{udetail.professional.services.join(", ")}</strong></p>}
                            {udetail.professional.bio && <p className="sm:col-span-2"><span className="text-[#78716c]">Bio:</span> <span className="text-[#57534e]">{udetail.professional.bio}</span></p>}
                          </div>
                        ) : <div className="text-sm text-[#78716c]">Nenhum perfil criado.</div>}
                      </div>

                      {/* Rentals */}
                      <div>
                        <p className="font-head font-semibold text-[#1c1917] mb-1">Imóveis ({udetail.rentals.length})</p>
                        {udetail.rentals.length === 0 ? <div className="text-sm text-[#78716c]">Nenhum imóvel.</div> : (
                          <div className="space-y-2">
                            {udetail.rentals.map((r) => (
                              <div key={r.id} className="text-sm border border-[#e7e5e4] rounded-lg p-2 bg-white">
                                <div className="text-[#1c1917]">{r.title} · €{Number(r.price).toLocaleString("pt-PT")}/mês · {r.city}, {r.region} · <span className={r.status === "active" ? "text-[#1b5e3f]" : "text-[#d97706]"}>{r.status === "active" ? "Publicado" : "Aguardando pagamento"}</span></div>
                                <div className="text-xs text-[#78716c] mt-0.5">CPF: {r.cpf || "—"} · Doc.: {r.id_document || "—"} · Contacto: {r.contact_preference || "—"} · Procura: {r.seeking_gender || "—"} · Permanência: {r.min_stay || "—"}</div>
                                <div className="flex flex-wrap gap-3 mt-1 text-xs">
                                  {[["Residência", r.residence_doc], ["Contrato", r.contract_doc], ["Selfie", r.selfie]].map(([lb, d], i) => d?.url ? <a key={i} href={docHref(d)} target="_blank" rel="noreferrer" className="text-[#1b5e3f] underline">{lb}</a> : <span key={i} className="text-[#d97706]">{lb}: não enviado</span>)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Ads */}
                      <div>
                        <p className="font-head font-semibold text-[#1c1917] mb-1">Anúncios ({udetail.ads.length})</p>
                        {udetail.ads.length === 0 ? <div className="text-sm text-[#78716c]">Nenhum anúncio.</div> : (
                          <div className="text-sm text-[#78716c]">{udetail.ads.map((a) => a.title).join(", ")}</div>
                        )}
                      </div>

                      {/* Actions inside detail */}
                      {u.role !== "admin" && (
                        <div className="flex flex-wrap gap-2 pt-3 border-t border-[#e7e5e4]">
                          <button data-testid={`detail-verify-${u.user_id}`} onClick={() => toggleVerify(u)} className={`text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1 ${u.verified ? "border border-[#e7e5e4] text-[#78716c] hover:bg-[#fafaf8]" : "bg-[#1b5e3f] text-white hover:bg-[#13422c]"}`}><CheckCircle2 className="w-3.5 h-3.5" /> {u.verified ? "Remover verificação" : "Verificar"}</button>
                          <button data-testid={`detail-request-info-${u.user_id}`} onClick={() => requestInfo(u)} className="text-xs px-3 py-1.5 rounded-lg border border-[#f59e0b] text-[#d97706] hover:bg-[#fef9ec] inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Solicitar informações</button>
                          <button data-testid={`detail-edit-${u.user_id}`} onClick={() => setUedit({ user_id: u.user_id, name: u.name, email: u.email, role: u.role })} className="text-xs px-3 py-1.5 rounded-lg border border-[#e7e5e4] text-[#1c1917] hover:bg-[#fafaf8] inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                          <button data-testid={`detail-delete-${u.user_id}`} onClick={() => delUser(u)} className="text-xs px-3 py-1.5 rounded-lg border border-[#ef4444] text-[#ef4444] hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Subscription Tab ----------
const STRIPE_SUB_LINKS = {
  monthly: "https://buy.stripe.com/bJe6oH1uT0v6aoYcKw6Vq04",
  promo: "https://buy.stripe.com/eVq3cv0qPb9KaoY6m86Vq02",
};

function SubscriptionTab() {
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState({ plans: [], promo_available: false });
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/subscription/plans").then((r) => { setData(r.data); if (r.data.plans?.[0]) setPlan((p) => p || r.data.plans[0].id); });
    api.get("/me/subscription").then((r) => setStatus(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || params.get("tab") !== "subscription") return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data: st } = await api.get(`/payments/status/${sid}`);
        if (st.payment_status === "paid") {
          toast.success("Pagamento confirmado! Seu cadastro está ativo.");
          params.delete("session_id"); setParams(params, { replace: true });
          await checkAuth(); load(); return;
        }
        if (st.status === "expired") { toast.error("Pagamento expirado."); return; }
      } catch {}
      if (attempts < 6) setTimeout(poll, 2000); else toast.info("Verifique o status da assinatura em instantes.");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async () => {
    if (!plan) return;
    setLoading(true);
    try {
      const { data: res } = await api.post("/subscription/checkout", { plan, origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="font-head text-xl font-semibold text-[#1c1917]">Assinatura</h2>
        <p className="text-sm text-[#78716c]">Mantenha o seu cadastro ativo na plataforma.</p>
      </div>

      {status && (
        <div data-testid="subscription-status" className={`rounded-2xl border p-5 ${status.status === "active" ? "bg-[#e8f0ec] border-[#1b5e3f]/20" : "bg-[#fef9ec] border-[#fef3c7]"}`}>
          {status.status === "active" ? (
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-[#1b5e3f]" />
              <div>
                <p className="font-medium text-[#1c1917]">Cadastro ativo — plano {SUB_LABELS[status.plan] || status.plan}</p>
                <p className="text-sm text-[#78716c]">Válido até <strong>{fmtDate(status.expires_at)}</strong></p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#d97706]" />
              <p className="text-[#1c1917]">{status.status === "expired" ? `Seu cadastro expirou em ${fmtDate(status.expires_at)}. Renove para continuar ativo.` : "Você ainda não possui uma assinatura ativa."}</p>
            </div>
          )}
        </div>
      )}

      {data.promo_available && (
        <div className="bg-[#1b5e3f] text-white rounded-2xl p-4 text-sm">
          🎉 <strong>Promoção de lançamento ativa!</strong> Assine agora por apenas <strong className="text-[#f59e0b]">10€</strong> e fique 1 ano inteiro cadastrado.
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        {data.plans.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`sub-plan-${p.id}`}
            onClick={() => setPlan(p.id)}
            className={`text-left rounded-2xl border-2 p-5 transition-all ${plan === p.id ? "border-[#1b5e3f] bg-[#e8f0ec]" : "border-[#e7e5e4] bg-white hover:border-[#1b5e3f]/40"}`}
          >
            {p.id === "promo" && <span className="text-[10px] uppercase font-bold text-[#d97706] bg-[#fef3c7] px-2 py-0.5 rounded-full">Lançamento</span>}
            <div className="font-head font-bold text-[#1c1917] mt-1">{p.label}</div>
            <div className="text-3xl font-head font-bold text-[#1b5e3f] mt-1">€{Number(p.amount).toLocaleString("pt-PT")}</div>
            <div className="text-xs text-[#78716c] mt-1">{p.desc}</div>
            <div className="text-xs text-[#78716c]">{p.days >= 365 ? "Acesso por 1 ano" : "Acesso por 30 dias"}</div>
          </button>
        ))}
      </div>

      <Button data-testid="subscription-checkout-btn" onClick={checkout} disabled={loading || !plan} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{status?.status === "active" ? "Renovar / estender" : "Assinar agora"}</>}
      </Button>

      {plan && STRIPE_SUB_LINKS[plan] && (
        <a
          href={withRef(STRIPE_SUB_LINKS[plan], user)}
          target="_blank"
          rel="noreferrer"
          data-testid="subscription-annual-voucher"
          className="block"
        >
          <div className="rounded-2xl border-2 border-dashed border-[#f59e0b] bg-[#fef9ec] p-5 flex items-center gap-4 hover:bg-[#fef3c7] transition-colors">
            <div className="w-12 h-12 rounded-xl bg-[#f59e0b] flex items-center justify-center shrink-0">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-head font-semibold text-[#1c1917]">Voucher de pagamento — Plano {SUB_LABELS[plan] || plan}</p>
              <p className="text-sm text-[#92400e]">Clique para pagar com segurança pelo Stripe.</p>
            </div>
            <span className="ml-auto text-sm font-semibold text-[#1b5e3f] underline shrink-0">Pagar →</span>
          </div>
        </a>
      )}
      <p className="text-xs text-[#78716c]">Pagamento único e seguro via Stripe. O período é somado ao seu prazo atual.</p>
    </div>
  );
}

const SUB_LABELS = { promo: "Promocional", monthly: "Mensal", annual: "Anual" };

// ---------- Advertiser Showcase (official €1200/ano ads shown to every user) ----------
function AdvertiserShowcase() {
  const [ads, setAds] = useState([]);
  useEffect(() => {
    api.get("/ads").then((r) => setAds((r.data || []).filter((a) => a.package === "anunciante"))).catch(() => {});
  }, []);
  if (ads.length === 0) return null;
  return (
    <div data-testid="advertiser-showcase" className="mb-8 bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-5 h-5 text-[#1b5e3f]" />
        <h2 className="font-head font-semibold text-[#1c1917]">Anunciantes oficiais do portal</h2>
        <span className="text-[10px] uppercase font-bold text-[#d97706] bg-[#fef3c7] px-2 py-0.5 rounded-full">Patrocinado</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ads.map((a) => {
          const card = (
            <div data-testid={`showcase-ad-${a.id}`} className="group rounded-2xl border border-[#e7e5e4] overflow-hidden bg-[#fafaf8] hover:shadow-md transition-shadow h-full">
              {a.image_url && <div className="aspect-[16/9] bg-[#e8f0ec] overflow-hidden"><img src={a.image_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /></div>}
              <div className="p-4">
                <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-[#1b5e3f] mb-1"><CheckCircle2 className="w-3 h-3" /> Anunciante oficial</div>
                <p className="font-medium text-[#1c1917]">{a.title}</p>
                {a.description && <p className="text-sm text-[#78716c] line-clamp-2 mt-1">{a.description}</p>}
              </div>
            </div>
          );
          return a.link_url ? (
            <a key={a.id} href={a.link_url} target="_blank" rel="noreferrer" className="block">{card}</a>
          ) : (
            <div key={a.id}>{card}</div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Advertiser Tab ----------
const STRIPE_ADVERTISER_LINKS = {
  mensal: "https://buy.stripe.com/9B6fZhflJb9K54E39W6Vq06",
  anual: "https://buy.stripe.com/28E5kDflJelW0Oo6m86Vq03",
};
const ADV_PLAN_LABELS = { mensal: "Mensal (150€/mês)", anual: "Anual (1200€/ano)" };

function AdvertiserTab() {
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState({ plans: [] });
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/advertiser/plans").then((r) => { setData(r.data); if (r.data.plans?.[0]) setPlan((p) => p || r.data.plans[0].id); });
    api.get("/me/advertiser").then((r) => setStatus(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || params.get("tab") !== "advertiser") return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data: st } = await api.get(`/payments/status/${sid}`);
        if (st.payment_status === "paid") {
          toast.success("Pagamento confirmado! Você agora é um anunciante oficial.");
          params.delete("session_id"); setParams(params, { replace: true });
          await checkAuth(); load(); return;
        }
        if (st.status === "expired") { toast.error("Pagamento expirado."); return; }
      } catch {}
      if (attempts < 6) setTimeout(poll, 2000); else toast.info("Verifique o status em instantes.");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async () => {
    if (!plan) return;
    setLoading(true);
    try {
      const { data: res } = await api.post("/advertiser/checkout", { plan, origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  const [advAds, setAdvAds] = useState([]);
  const [adForm, setAdForm] = useState({ title: "", description: "", link_url: "" });
  const [adImg, setAdImg] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const loadAds = useCallback(() => api.get("/me/ads").then((r) => setAdvAds(r.data.filter((a) => a.package === "anunciante"))), []);
  useEffect(() => { loadAds(); }, [loadAds]);
  const publishAd = async (e) => {
    e.preventDefault(); setPublishing(true);
    try {
      await api.post("/me/advertiser-ad", { title: adForm.title, description: adForm.description, image_url: adImg[0]?.url || "", link_url: adForm.link_url });
      toast.success("Propaganda publicada! Já aparece na página de Propagandas.");
      setAdForm({ title: "", description: "", link_url: "" }); setAdImg([]); loadAds();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPublishing(false); }
  };
  const delAdvAd = async (id) => { if (!window.confirm("Excluir esta propaganda?")) return; await api.delete(`/me/ads/${id}`); toast.success("Propaganda removida."); loadAds(); };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="font-head text-xl font-semibold text-[#1c1917]">Seja um Anunciante Oficial</h2>
        <p className="text-sm text-[#78716c]">Tenha presença premium e divulgue a sua marca para toda a comunidade Brasil–Portugal.</p>
        <a href="/propagandas" data-testid="advertiser-view-propagandas" className="text-sm text-[#1b5e3f] font-medium hover:underline">Ver página pública de Propagandas →</a>
      </div>

      {status && (
        <div data-testid="advertiser-status" className={`rounded-2xl border p-5 ${status.status === "active" ? "bg-[#e8f0ec] border-[#1b5e3f]/20" : "bg-[#fef9ec] border-[#fef3c7]"}`}>
          {status.status === "active" ? (
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-[#1b5e3f]" />
              <div>
                <p className="font-medium text-[#1c1917]">Anunciante oficial ativo</p>
                <p className="text-sm text-[#78716c]">Válido até <strong>{fmtDate(status.expires_at)}</strong></p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#d97706]" />
              <p className="text-[#1c1917]">{status.status === "expired" ? `Seu plano de anunciante expirou em ${fmtDate(status.expires_at)}.` : "Você ainda não é um anunciante oficial."}</p>
            </div>
          )}
        </div>
      )}

      {data.promo_available && (
        <div className="bg-[#1b5e3f] text-white rounded-2xl p-4 text-sm">
          🎉 <strong>Seja anunciante oficial!</strong> Escolha o plano que preferir: <strong className="text-[#f59e0b]">150€/mês</strong> ou <strong className="text-[#f59e0b]">1200€/ano</strong>.
        </div>
      )}

      {status?.status === "active" && (
        <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#1b5e3f]" />
            <h3 className="font-head font-semibold text-[#1c1917]">Publicar propaganda (grátis para anunciantes oficiais)</h3>
          </div>
          <p className="text-sm text-[#78716c] -mt-2">Sua propaganda aparece automaticamente na página pública de Propagandas, em destaque como oficial.</p>
          <form onSubmit={publishAd} className="space-y-4">
            <Field label="Título"><input data-testid="adv-ad-title" required className={inputCls} value={adForm.title} onChange={(e) => setAdForm({ ...adForm, title: e.target.value })} /></Field>
            <Field label="Descrição"><textarea rows={2} className={inputCls} value={adForm.description} onChange={(e) => setAdForm({ ...adForm, description: e.target.value })} /></Field>
            <Field label="Link de destino (opcional)"><input className={inputCls} placeholder="https://..." value={adForm.link_url} onChange={(e) => setAdForm({ ...adForm, link_url: e.target.value })} /></Field>
            <FileUpload label="Imagem / banner" hint="JPG ou PNG" accept="image/*" category="photo" value={adImg} onChange={setAdImg} testid="adv-ad-image" />
            <Button data-testid="adv-ad-publish-btn" type="submit" disabled={publishing} className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706]">{publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Publicar propaganda</>}</Button>
          </form>
          {advAds.length > 0 && (
            <div className="space-y-2 pt-4 border-t border-[#e7e5e4]">
              <p className="text-sm font-medium text-[#1c1917]">Minhas propagandas publicadas</p>
              {advAds.map((a) => (
                <div key={a.id} data-testid={`adv-ad-${a.id}`} className="flex items-center justify-between bg-[#fafaf8] border border-[#e7e5e4] rounded-xl px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {a.image_url && <img src={a.image_url} className="w-9 h-9 rounded-lg object-cover" alt="" />}
                    <span className="text-sm text-[#1c1917] truncate">{a.title}</span>
                  </div>
                  <button onClick={() => delAdvAd(a.id)} data-testid={`adv-ad-delete-${a.id}`} className="text-[#78716c] hover:text-[#ef4444]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4">
        {data.plans.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`advertiser-plan-${p.id}`}
            onClick={() => setPlan(p.id)}
            className={`text-left rounded-2xl border-2 p-6 transition-all ${plan === p.id ? "border-[#1b5e3f] bg-[#e8f0ec]" : "border-[#e7e5e4] bg-white hover:border-[#1b5e3f]/40"}`}
          >
            {data.promo_available && <span className="text-[10px] uppercase font-bold text-[#d97706] bg-[#fef3c7] px-2 py-0.5 rounded-full">Promoção de lançamento</span>}
            <div className="font-head font-bold text-[#1c1917] mt-1">{p.label}</div>
            <div className="text-4xl font-head font-bold text-[#1b5e3f] mt-1">€{Number(p.amount).toLocaleString("pt-PT")}<span className="text-base font-normal text-[#78716c]">/{p.id === "mensal" ? "mês" : "ano"}</span></div>
            <div className="text-sm text-[#78716c] mt-1">{p.desc}</div>
            <ul className="text-sm text-[#78716c] mt-3 space-y-1">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#1b5e3f]" /> Marca em destaque no portal</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#1b5e3f]" /> Selo de anunciante oficial</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#1b5e3f]" /> Acesso por {p.id === "mensal" ? "1 mês" : "1 ano"}</li>
            </ul>
          </button>
        ))}
      </div>

      <Button data-testid="advertiser-checkout-btn" onClick={checkout} disabled={loading || !plan} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{status?.status === "active" ? "Renovar / estender" : "Tornar-se anunciante"}</>}
      </Button>

      {plan && STRIPE_ADVERTISER_LINKS[plan] && (
        <a
          href={withRef(STRIPE_ADVERTISER_LINKS[plan], user)}
          target="_blank"
          rel="noreferrer"
          data-testid="advertiser-voucher"
          className="block"
        >
          <div className="rounded-2xl border-2 border-dashed border-[#f59e0b] bg-[#fef9ec] p-5 flex items-center gap-4 hover:bg-[#fef3c7] transition-colors">
            <div className="w-12 h-12 rounded-xl bg-[#f59e0b] flex items-center justify-center shrink-0">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-head font-semibold text-[#1c1917]">Voucher de pagamento — Anunciante {ADV_PLAN_LABELS[plan] || plan}</p>
              <p className="text-sm text-[#92400e]">Clique para pagar o plano de anunciante com segurança pelo Stripe.</p>
            </div>
            <span className="ml-auto text-sm font-semibold text-[#1b5e3f] underline shrink-0">Pagar →</span>
          </div>
        </a>
      )}
      <p className="text-xs text-[#78716c]">Pagamento único e seguro via Stripe. O período é somado ao seu prazo atual.</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [meta, setMeta] = useState({});
  const [tab, setTab] = useState(params.get("tab") || "profile");
  const [unread, setUnread] = useState(0);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/me/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [user, tab]);
  const refreshUnread = useCallback(() => {
    api.get("/me/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  }, []);

  const dismissInfoRequest = async () => {
    try { await api.post("/me/info-request/dismiss"); toast.success("Solicitação marcada como resolvida."); await checkAuth(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf8]"><Loader2 className="w-8 h-8 animate-spin text-[#1b5e3f]" /></div>;

  const tabs = [
    { id: "profile", label: "Meu Perfil", icon: User },
    { id: "subscription", label: "Assinatura", icon: Crown },
    { id: "advertiser", label: "Anunciante", icon: Building2 },
    { id: "rentals", label: "Meus Imóveis", icon: Home },
    { id: "jobs", label: "Ofertas de Trabalho", icon: Briefcase },
    { id: "ads", label: "Anúncios", icon: Megaphone },
    { id: "messages", label: "Mensagens", icon: MessageSquare },
  ];
  if (user.role === "admin") tabs.push({ id: "admin", label: "Admin", icon: Shield });
  if (user.role === "admin") tabs.push({ id: "control", label: "Controle", icon: BarChart3 });

  const sub = user.subscription || { status: "none" };
  const daysLeft = sub.status === "active" && sub.expires_at
    ? Math.ceil((new Date(sub.expires_at) - new Date()) / 86400000)
    : null;
  const expiringSoon = daysLeft !== null && daysLeft <= 7;
  const isCourtesy = sub.plan === "courtesy";

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#1b5e3f] flex items-center justify-center overflow-hidden">
            {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-white font-head font-bold text-lg">{(user.name || "?")[0]}</span>}
          </div>
          <div>
            <h1 className="font-head text-2xl font-semibold text-[#1c1917]">Olá, {user.name?.split(" ")[0] || "usuário"}</h1>
            <p className="text-sm text-[#78716c]">{user.email}</p>
          </div>
          {sub.status === "active" ? (
            <span data-testid="membership-badge-active" className="ml-auto text-xs bg-[#e8f0ec] text-[#1b5e3f] px-3 py-1.5 rounded-full inline-flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> Cadastro ativo</span>
          ) : (
            <span data-testid="membership-badge-inactive" className="ml-auto text-xs bg-[#fef3c7] text-[#d97706] px-3 py-1.5 rounded-full inline-flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> {sub.status === "expired" ? "Cadastro expirado" : "Sem assinatura"}</span>
          )}
          {user.verified && (
            <span data-testid="verified-badge" className="text-xs bg-[#dbeafe] text-[#2563eb] px-3 py-1.5 rounded-full inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> Perfil verificado</span>
          )}
        </div>

        {expiringSoon && (
          <div data-testid="subscription-expiring-banner" className="mb-8 rounded-2xl border border-[#fed7aa] bg-[#fff7ed] p-5 flex items-start gap-3">
            <Clock className="w-5 h-5 text-[#ea580c] mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-head font-semibold text-[#1c1917]">
                {isCourtesy ? "Sua cortesia de 1 mês está terminando" : "Sua assinatura está prestes a expirar"}
              </p>
              <p className="text-sm text-[#9a3412] mt-1">
                {daysLeft <= 0
                  ? "Expira hoje. "
                  : `Faltam ${daysLeft} dia${daysLeft > 1 ? "s" : ""} (expira em ${fmtDate(sub.expires_at)}). `}
                {isCourtesy
                  ? "Assine agora para continuar aparecendo no diretório após o término da cortesia."
                  : "Renove para não perder sua presença no diretório."}
              </p>
            </div>
            <button data-testid="subscription-renew-btn" onClick={() => setTab("subscription")} className="text-sm px-4 py-2 rounded-xl bg-[#ea580c] text-white hover:bg-[#c2410c] shrink-0 inline-flex items-center gap-1"><Crown className="w-4 h-4" /> {isCourtesy ? "Assinar agora" : "Renovar"}</button>
          </div>
        )}

        {user.info_request?.message && (
          <div data-testid="user-info-request-banner" className="mb-8 rounded-2xl border border-[#fef3c7] bg-[#fef9ec] p-5 flex items-start gap-3">
            <FileText className="w-5 h-5 text-[#d97706] mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-head font-semibold text-[#1c1917]">O administrador solicitou informações</p>
              <p className="text-sm text-[#92400e] mt-1">{user.info_request.message}</p>
            </div>
            <button data-testid="dismiss-info-request-btn" onClick={dismissInfoRequest} className="text-xs px-3 py-1.5 rounded-lg border border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec] shrink-0">Marcar como resolvido</button>
          </div>
        )}

        <AdvertiserShowcase />

        <div className="flex gap-2 mb-8 overflow-x-auto scrollbar-hide">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`relative px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap inline-flex items-center gap-2 transition-colors ${tab === t.id ? "bg-[#1b5e3f] text-white" : "bg-white border border-[#e7e5e4] text-[#78716c] hover:text-[#1c1917]"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.id === "messages" && unread > 0 && (
                <span data-testid="messages-unread-badge" className="text-[10px] font-bold bg-[#f59e0b] text-white rounded-full min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center">{unread}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "profile" && <ProfileTab meta={meta} />}
        {tab === "subscription" && <SubscriptionTab />}
        {tab === "advertiser" && <AdvertiserTab />}
        {tab === "rentals" && <RentalsTab meta={meta} />}
        {tab === "jobs" && <JobOffersTab meta={meta} />}
        {tab === "ads" && <AdsTab />}
        {tab === "messages" && <MessagesTab onRead={refreshUnread} />}
        {tab === "admin" && user.role === "admin" && <AdminTab />}
        {tab === "control" && user.role === "admin" && <ControlPanel />}
      </div>
      <Footer />
    </div>
  );
}
